import React, { useEffect } from 'react';
import { PrismButton, PrismIcon, PrismText } from '@prism-ui/react';
import { PageSection } from 'adc-ui-components';
import { kibanaLog } from '../helpers/logger';
import { flagEnabled } from '../helpers/featureFlags';

/**
 * Full page error state for Prism-based flows.
 * Replaces the current page content when an unrecoverable error occurs.
 *
 * @param {Object} props
 * @param {string} [props.title] - Error heading.
 * @param {string} [props.subtitle] - Error message shown below the heading.
 * @param {string} [props.buttonText] - Label for the action button.
 * @param {string} [props.buttonHref] - Optional CTA URL. When provided, button renders as a link.
 * @param {string} [props.className] - Additional CSS class on the PageSection wrapper.
 * @param {function(): void} props.onButtonClick - Handler for the action button.
 */
const FullPageError = ({
  title = "Looks like that didn't work",
  subtitle = 'Try again later if the problem continues.',
  body,
  buttonText = 'Refresh',
  buttonHref,
  className = 'prism-design',
  iconName = 'alert',
  error,
  isErrorBoundary = false,
  onButtonClick = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  },
}) => {
  useEffect(() => {
    if (!isErrorBoundary) {
      kibanaLog('generic_error', error);
    }
    if (!flagEnabled('xmPaymentReskinEnabled')) return undefined;

    const alreadyHadBodyPrism = document.body.classList.contains('body--prism');
    const alreadyHadDocumentElementPrism = document.documentElement.classList.contains('prism');

    document.body.classList.add('body--prism');
    document.documentElement.classList.add('prism');

    return () => {
      if (!alreadyHadBodyPrism) {
        document.body.classList.remove('body--prism');
      }
      if (!alreadyHadDocumentElementPrism) {
        document.documentElement.classList.remove('prism');
      }
    };
  }, [error, isErrorBoundary]);

  return (
    <PageSection className={className}>
      <div className="mb-8 mt-10">
        <PrismIcon
          name={iconName}
          display="outline"
          size="xl"
          tone="danger"
          className="mb-4"
        />
        <PrismText tag="h1" family="display" size="200" className="mb-4">
          {title}
        </PrismText>
        {body || (subtitle && (
          <PrismText tag="p" family="body" size="400">
            {subtitle}
          </PrismText>
        ))}
      </div>
      <PrismButton
        display="fill"
        tone="primary"
        {...(buttonHref
          ? { href: buttonHref }
          : { type: 'button', onClick: onButtonClick })}
      >
        {buttonText}
      </PrismButton>
    </PageSection>
  );
};
export default FullPageError;
